/**
 * Ctrl+K command palette (SellerMate W0.2) — "take me to X" from anywhere.
 *
 * Mounted ONCE in App.tsx's header. It renders a small "Search" button there
 * and, when opened (the button, or Ctrl+K / Cmd+K anywhere), a dialog that
 * searches two things at once:
 *
 *   - Pages: every screen in the sidebar catalog (components/sidebarConfig.tsx),
 *     matched in the browser as you type;
 *   - Records: campaigns, ad groups, keywords, targets, products (ASIN / UID /
 *     SKU), rules and purchase orders, from GET /api/search
 *     (backend/api/command_search.py), debounced.
 *
 * Up / Down move, Enter opens the highlighted row, Ctrl+Enter (Cmd+Enter)
 * asks the typed text in the AI side panel, Esc closes. The
 * decisions live in lib/commandPalette.ts so they are tested without a DOM.
 *
 * Read-only: opening a row only navigates. Nothing here changes a bid, a rule
 * or anything on Amazon.
 */
import { type KeyboardEvent, type ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search, Loader2, Sparkles, FileText, CornerDownLeft } from 'lucide-react'
import { api } from '../lib/api'
import { useApiQuery } from '../lib/query/useApiQuery'
import { keys } from '../lib/query/keys'
import { useDebouncedValue } from '../lib/useDebouncedValue'
import { useAmcEnabled } from '../lib/useAmcStatus'
import { useAuth } from '../lib/auth'
import { openAiPanel } from '../lib/aiPanel'
import { ADS_TABS } from '../pages/ads/routing'
import { useCommandPaletteHotkey } from '../ops/lib/useKeyboardShortcuts'
import { ITEM_CATALOG, LABS_ADMIN_ITEMS } from './sidebarConfig'
import {
  MIN_QUERY_LEN, buildGroups, clampIndex, currentResult, flattenRows, matchPages,
  paletteKeyAction, askPanelOpts, type PageEntry, type PaletteRow,
} from '../lib/commandPalette'
import { cn } from '../lib/cn'

/** Sidebar items the sidebar hides behind a feature flag (visibleTree). */
const AMC_GATED = new Set(['audiences'])

/** The palette's backdrop: the scrim token DIMS the page behind it. (It was
 *  `bg-overlay`, a SURFACE token - opaque white - so opening the palette blanked
 *  the page, D5.) Exported for the markup test. */
export const PALETTE_BACKDROP_CLASS =
  'fixed inset-0 z-50 flex items-start justify-center bg-scrim px-4 pt-[12vh]'

export default function CommandPalette() {
  const [open, setOpen] = useState(false)
  useCommandPaletteHotkey(() => setOpen(true))
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="mr-auto h-8 w-64 max-w-[40vw] inline-flex items-center gap-2 px-3 rounded-lg border border-edge bg-surface-2 text-sm text-ink-faint hover:text-ink-muted hover:border-edge-strong transition-colors"
        aria-label="Search pages and records (Ctrl+K)"
        title="Search pages and records (Ctrl+K)"
      >
        <Search size={14} aria-hidden />
        <span className="flex-1 text-left truncate">Search or jump to…</span>
        <kbd className="text-[10px] font-mono px-1.5 py-0.5 rounded border border-edge bg-surface text-ink-muted">Ctrl K</kbd>
      </button>
      {open && <PaletteDialog onClose={() => setOpen(false)} />}
    </>
  )
}

/** Palette-only rows for the Advertising hub's levels (PR 6): the sidebar has
 *  one Advertising item now, but "targets" or "negatives" should still find a
 *  page. */
const ADS_LEVEL_PAGES: PageEntry[] = ADS_TABS.map(t => ({
  id: `ads-${t.key}`, label: `Advertising › ${t.label}`, path: `/ads?tab=${t.key}`,
}))

function PaletteDialog({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate()
  const amcEnabled = useAmcEnabled()
  const { isAdmin } = useAuth()
  const [q, setQ] = useState('')
  const [index, setIndex] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)

  useEffect(() => { inputRef.current?.focus() }, [])

  const pages = useMemo<PageEntry[]>(
    () => [
      // The same gates as the sidebar: Audiences needs AMC, the Labs admin
      // tools are for admins (Workflows stays open to everyone).
      ...Object.entries(ITEM_CATALOG)
        .filter(([id]) => (amcEnabled || !AMC_GATED.has(id)) && (isAdmin || !LABS_ADMIN_ITEMS.has(id)))
        .map(([id, e]) => ({ id, label: e.defaultLabel, path: e.path })),
      ...ADS_LEVEL_PAGES,
    ],
    [amcEnabled, isAdmin],
  )

  const debounced = useDebouncedValue(q.trim(), 200)
  const searching = debounced.length >= MIN_QUERY_LEN
  // No keepPrevious: the last query's records are never shown for a new one
  // (currentResult) - listed, they were one Enter from opening the wrong record.
  const records = useApiQuery(
    keys.search.palette(debounced),
    () => api.commandSearch(debounced),
    { enabled: searching, retry: 0 },
  )
  const result = searching ? currentResult(records.data, q) : undefined

  const groups = useMemo(
    () => buildGroups(q, matchPages(pages, q), result),
    [q, pages, result],
  )
  const rows = useMemo(() => flattenRows(groups), [groups])
  // Where each group's rows start in the flat list Up/Down walks.
  const starts = useMemo(() => {
    const out: number[] = []
    let n = 0
    for (const g of groups) { out.push(n); n += g.rows.length }
    return out
  }, [groups])
  const active = clampIndex(index, rows.length)

  // Keep the highlighted row in view as Up/Down walk past the edge.
  useEffect(() => {
    const el = listRef.current?.querySelector<HTMLElement>(`[data-row-index="${active}"]`)
    el?.scrollIntoView({ block: 'nearest' })
  }, [active])

  const go = (row: PaletteRow | undefined) => {
    if (!row) return
    onClose()
    // "Ask AI" asks in the side panel; it is no longer a page to navigate to.
    if (row.type === 'ask') { openAiPanel(askPanelOpts(q)); return }
    navigate(row.href)
  }

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    const action = paletteKeyAction(e, active, rows.length)
    if (!action) return
    e.preventDefault()
    e.stopPropagation()
    switch (action.type) {
      case 'close':
        onClose()
        return
      case 'move':
        setIndex(action.index)
        return
      case 'open':
        go(rows[action.index])
        return
      case 'ask':
        if (!q.trim()) return
        onClose()
        openAiPanel(askPanelOpts(q))
        return
    }
  }

  const loading = searching && records.isFetching
  // Only an answer to the typed text can say "no match" (while a keystroke is
  // still being debounced, the answer in hand is for the text before it).
  const noMatches = searching && !loading && result !== undefined
    && groups.every(g => g.key === 'ask')

  return (
    <div
      className={PALETTE_BACKDROP_CLASS}
      onMouseDown={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Search pages and records"
        className="w-full max-w-xl rounded-xl border border-edge bg-surface text-ink shadow-2xl overflow-hidden"
        onMouseDown={e => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 px-4 border-b border-edge">
          <Search size={16} className="text-ink-faint shrink-0" aria-hidden />
          <input
            ref={inputRef}
            value={q}
            onChange={e => { setQ(e.target.value); setIndex(0) }}
            onKeyDown={onKeyDown}
            placeholder="Search pages, campaigns, ASINs, UIDs, rules, POs…"
            className="flex-1 h-12 bg-transparent text-sm text-ink placeholder:text-ink-faint focus:outline-none"
            role="combobox"
            aria-expanded={rows.length > 0}
            aria-controls="command-palette-list"
            aria-activedescendant={active >= 0 ? `command-palette-row-${active}` : undefined}
          />
          {loading && <Loader2 size={14} className="animate-spin text-ink-faint" aria-label="Searching" />}
        </div>

        <div ref={listRef} id="command-palette-list" role="listbox" className="max-h-[55vh] overflow-y-auto py-1">
          {!q.trim() && (
            <p className="px-4 py-6 text-sm text-ink-muted">
              Type a page name, campaign, keyword, ASIN, UID, rule or PO number.
              Press Ctrl+Enter to ask the AI assistant in the side panel.
            </p>
          )}
          {q.trim().length > 0 && q.trim().length < MIN_QUERY_LEN && (
            <p className="px-4 pt-3 pb-1 text-xs text-ink-faint">Type one more character to search records.</p>
          )}
          {records.isError && searching && (
            <p className="px-4 pt-3 pb-1 text-xs text-danger">Records could not be searched right now. Pages still work.</p>
          )}
          {noMatches && (
            <p className="px-4 pt-3 pb-1 text-xs text-ink-muted">No pages or records match “{debounced}”.</p>
          )}
          {groups.map((g, gi) => (
            <div key={g.key} role="group" aria-label={g.label} className="py-1">
              <div className="px-4 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-ink-faint">
                {g.label}
              </div>
              {g.rows.map((row, ri) => {
                const i = starts[gi] + ri
                const selected = i === active
                return (
                  <div
                    key={row.key}
                    id={`command-palette-row-${i}`}
                    data-row-index={i}
                    role="option"
                    aria-selected={selected}
                    onMouseMove={() => { if (!selected) setIndex(i) }}
                    onClick={() => go(row)}
                    className={cn(
                      'mx-1 px-3 py-2 rounded-lg flex items-center gap-3 cursor-pointer',
                      selected ? 'bg-accent-soft text-accent-soft-fg' : 'text-ink',
                    )}
                  >
                    <RowIcon row={row} />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm truncate">{row.label}</div>
                      {row.sublabel && (
                        <div className={cn('text-xs truncate', selected ? 'text-accent-soft-fg' : 'text-ink-muted')}>
                          {row.sublabel}
                        </div>
                      )}
                    </div>
                    {selected && <CornerDownLeft size={14} className="shrink-0" aria-hidden />}
                  </div>
                )
              })}
              {g.note && <div className="px-4 pt-1 text-xs text-ink-faint">{g.note}</div>}
            </div>
          ))}
        </div>

        <div className="flex items-center gap-4 px-4 py-2 border-t border-edge bg-surface-2 text-[11px] text-ink-faint">
          <span><Key>↑</Key> <Key>↓</Key> move</span>
          <span><Key>Enter</Key> open</span>
          <span><Key>Ctrl</Key> <Key>Enter</Key> ask AI</span>
          <span className="ml-auto"><Key>Esc</Key> close</span>
        </div>
      </div>
    </div>
  )
}

function RowIcon({ row }: { row: PaletteRow }) {
  const cls = 'shrink-0 text-ink-faint'
  if (row.type === 'ask') return <Sparkles size={14} className={cls} aria-hidden />
  if (row.type === 'page') return <FileText size={14} className={cls} aria-hidden />
  return (
    <span className="shrink-0 w-14 text-[10px] font-medium uppercase tracking-wide text-ink-faint">
      {KIND_TAG[row.kind]}
    </span>
  )
}

const KIND_TAG: Record<string, string> = {
  campaign: 'Campaign', ad_group: 'Ad group', keyword: 'Keyword', target: 'Target',
  product: 'Product', rule: 'Rule', po: 'PO',
}

function Key({ children }: { children: ReactNode }) {
  return <kbd className="px-1 py-px rounded border border-edge bg-surface font-mono text-[10px] text-ink-muted">{children}</kbd>
}
